import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import PersonCell from '../../../components/ui/PersonCell';

export const PERIODS = ['day_1_30', 'day_31_60', 'day_61_90'];
// Mirrors TR module's TrainingAssignment.Status — keep aligned with backend.
export const TR_STATUSES = ['assigned', 'in_progress', 'completed', 'cancelled', 'expired'];

const multiSelectFilter = (label, options, valueRender) => ({
  filter: true,
  sort: true,
  filterType: 'custom',
  filterOptions: {
    defaultOp: 'in',
    display: (filterList, onChange, index, column) => {
      const current = filterList[index] && filterList[index][0]
        ? filterList[index][0].split(',').filter(Boolean)
        : [];
      return (
        <FormControl variant="standard" fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            multiple
            value={current}
            onChange={(e) => {
              const v = e.target.value;
              const csv = Array.isArray(v) ? v.filter(Boolean).join(',') : '';
              filterList[index] = csv ? [csv] : [];
              onChange(filterList[index], index, column);
            }}
            renderValue={(selected) => selected.map((s) => valueRender ? valueRender(s) : s).join(', ')}
          >
            {options.map((o) => (
              <MenuItem key={o} value={o}>{valueRender ? valueRender(o) : o}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    },
  },
});

const boolFilter = (label, t) => ({
  filter: true, sort: false, filterType: 'custom',
  filterOptions: {
    defaultOp: 'eq',
    display: (filterList, onChange, index, column) => {
      const current = filterList[index] && filterList[index][0] !== undefined ? filterList[index][0] : '';
      return (
        <FormControl variant="standard" fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            value={current}
            onChange={(e) => {
              const v = e.target.value;
              filterList[index] = v === '' ? [] : [v];
              onChange(filterList[index], index, column);
            }}
          >
            <MenuItem value=""><em>{t('common.all')}</em></MenuItem>
            <MenuItem value="true">{t('common.yes')}</MenuItem>
            <MenuItem value="false">{t('common.no')}</MenuItem>
          </Select>
        </FormControl>
      );
    },
  },
});

const useObTrainingsApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/onboarding-trainings?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const markCompleted = useCallback(async (id) => {
    try { await api.post(`/v1/onboarding-trainings/${id}/complete`); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const markRetake = useCallback(async (id, reason) => {
    try { await api.post(`/v1/onboarding-trainings/${id}/retake`, { reason }); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const unassign = useCallback(async (id) => {
    try { await api.delete(`/v1/onboarding-trainings/${id}`); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  // Maps drawer form values → ObTrainingAssignRequest (assign create).
  const toPayload = useCallback((formData) => ({
    onboardingId: formData.onboardingId || null,
    trainingAssignmentId: formData.trainingAssignmentId || null,
    period: formData.period || 'day_1_30',
    isMandatory: formData.isMandatory === undefined ? true : !!formData.isMandatory,
    originalContentVersion: formData.originalContentVersion || null,
  }), []);

  const create = useCallback(async (formData) => {
    try { await api.post('/v1/onboarding-trainings', toPayload(formData)); refresh(); return true; }
    catch { return false; }
  }, [refresh, toPayload]);

  const columns = useMemo(() => {
    const periodLabel = (s) => t(`onboardingTraining.period.${s}`, { defaultValue: s });
    const trStatusLabel = (s) => t(`onboardingTraining.trStatus.${s}`, { defaultValue: s });

    return [
      { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
      {
        name: 'personFullName',
        label: t('onboardingTraining.person'),
        options: {
          customBodyRender: (v, tableMeta) => (
            <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.personProfilePicture} />
          ),
          filter: true, sort: true,
          filterType: 'textField',
          filterOptions: { defaultOp: 'like', filterKey: 'personName[like]' },
        },
      },
      {
        name: 'trainingTitle',
        label: t('onboardingTraining.training'),
        options: { filter: false, sort: true },
      },
      {
        name: 'period',
        label: t('onboardingTraining.periodLabel'),
        options: {
          ...multiSelectFilter(t('onboardingTraining.periodLabel'), PERIODS, periodLabel),
          customBodyRender: (v) => periodLabel(v),
        },
      },
      {
        name: 'isMandatory',
        label: t('onboardingTraining.mandatory'),
        options: { ...boolFilter(t('onboardingTraining.mandatory'), t), customBodyRender: (v) => v ? '✓' : '-' },
      },
      {
        name: 'trainingStatus',
        label: t('onboardingTraining.trStatusLabel'),
        options: {
          ...multiSelectFilter(t('onboardingTraining.trStatusLabel'), TR_STATUSES, trStatusLabel),
          customBodyRender: (v) => trStatusLabel(v),
        },
      },
      {
        name: 'retakeRequired',
        label: t('onboardingTraining.retake'),
        options: { ...boolFilter(t('onboardingTraining.retake'), t), customBodyRender: (v) => v ? '⚠' : '-' },
      },
      dateFilterColumn('assignedAt', t('onboardingTraining.assignedAt'), lang),
      {
        name: 'completedAt',
        label: t('onboardingTraining.completedAt'),
        options: { filter: false, sort: true, customBodyRender: (v) => v ? new Date(v).toLocaleString() : '-' },
      },
    ];
  }, [t, lang, data]);

  // Holds the training currently chosen in the drawer so the dependent
  // assignment selector (which has no live access to form state) can scope its
  // fetch. Updated via the trainingId field's deriveFields hook on every change.
  const selectedTrainingId = useRef(null);

  const objectSchema = useMemo(() => {
    const periodLabel = (s) => t(`onboardingTraining.period.${s}`, { defaultValue: s });
    return [
      {
        name: 'onboardingId', label: t('onboardingTraining.person'), type: 'asyncSelect', required: true,
        description: t('onboardingTraining.form.onboardingHint'),
        onSearch: async (q) => {
          const params = q ? { 'personName[like]': q, pageSize: 20 } : { pageSize: 20 };
          const res = await api.get('/v1/onboardings', { params });
          const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
          return items.map((o) => ({ id: o.id, name: o.personFullName }));
        },
        onGetById: async (id) => {
          const res = await api.get(`/v1/onboardings/${id}`);
          const o = res.data?.data || {};
          return { id, name: o.personFullName || '' };
        },
      },
      {
        name: 'trainingId', label: t('onboardingTraining.training'), type: 'asyncSelect', required: true, grid: 6,
        description: t('onboardingTraining.form.trainingHint'),
        // Record the chosen training (for the assignment selector) and clear any
        // previously chosen assignment from another training.
        deriveFields: (val) => { selectedTrainingId.current = val || null; return { trainingAssignmentId: null }; },
        onSearch: async (q) => {
          const params = q ? { 'title[like]': q, pageSize: 20 } : { pageSize: 20 };
          const res = await api.get('/v1/trainings', { params });
          const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
          return items.map((tr) => ({ id: tr.id, name: tr.title }));
        },
        onGetById: async (id) => {
          const res = await api.get(`/v1/trainings/${id}`);
          const tr = res.data?.data || {};
          return { id, name: tr.title || '' };
        },
      },
      {
        name: 'trainingAssignmentId', label: t('onboardingTraining.form.assignment'), type: 'asyncSelect', required: true, grid: 6,
        description: t('onboardingTraining.form.assignmentHint'),
        hidden: (form) => !form.trainingId,
        // Scoped to the training picked above (read from the ref — this component
        // gets no live form state). q filters the fetched assignments client-side.
        onSearch: async (q) => {
          const trainingId = selectedTrainingId.current;
          if (!trainingId) return [];
          const res = await api.get(`/v1/training-assignments/by-training/${trainingId}`, { params: { pageSize: 50 } });
          const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
          const filtered = q
            ? items.filter((a) => (a.userFullName || '').toLowerCase().includes(q.toLowerCase()))
            : items;
          return filtered.map((a) => ({ id: a.id, name: a.userFullName || a.trainingTitle || a.id, profilePicture: a.userProfilePicture }));
        },
      },
      {
        name: 'period', label: t('onboardingTraining.periodLabel'), type: 'select', required: true, grid: 6,
        defaultValue: 'day_1_30',
        options: PERIODS.map((p) => ({ id: p, name: periodLabel(p) })),
      },
      {
        name: 'isMandatory', label: t('onboardingTraining.mandatory'), type: 'checkbox', grid: 6,
        defaultValue: true,
      },
      {
        name: 'originalContentVersion', label: t('onboardingTraining.form.contentVersion'), type: 'text', grid: 6,
        description: t('onboardingTraining.form.contentVersionHint'),
      },
    ];
  }, [t]);

  return { fetching, data, pagination, columns, objectSchema, refresh, create, markCompleted, markRetake, unassign };
};

export default useObTrainingsApi;
